// ============================================================
// LyricsField
// ============================================================
// An inline, click-to-edit textarea shown below each bar for
// entering lyrics. Appears on hover or when lyrics are present.
//
// Design notes:
//   - Uses a textarea (not a div) so it handles word wrap natively
//   - Auto-resizes to fit content (no scrollbar)
//   - Displays in a lighter weight than chord symbols
//   - On print, shows without the editing chrome
//   - Pressing Escape blurs the field
//   - Pressing Tab moves to the next bar (via onNavigate callback)
// ============================================================

import React, {
  useRef, useEffect, useCallback, useState,
} from 'react';

// ------------------------------------------------------------
// PROPS
// ------------------------------------------------------------

interface LyricsFieldProps {
  /** Current lyrics value, null = no lyrics */
  value: string | null;
  /** Called when lyrics change */
  onChange: (value: string | null) => void;
  /** Whether the parent bar is currently hovered */
  showHint: boolean;
  /** Tab key: navigate to next bar */
  onTabNext?: () => void;
  /** Shift+Tab: navigate to previous bar */
  onTabPrev?: () => void;
}

// ------------------------------------------------------------
// AUTO-RESIZE HELPER
// ------------------------------------------------------------

function autoResize(el: HTMLTextAreaElement) {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

// ------------------------------------------------------------
// COMPONENT
// ------------------------------------------------------------

export function LyricsField({
  value,
  onChange,
  showHint,
  onTabNext,
  onTabPrev,
}: LyricsFieldProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  // Auto-resize on value change
  useEffect(() => {
    const el = textareaRef.current;
    if (el) autoResize(el);
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const v = e.target.value;
      onChange(v === '' ? null : v);
      autoResize(e.target);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        textareaRef.current?.blur();
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        if (e.shiftKey) {
          onTabPrev?.();
        } else {
          onTabNext?.();
        }
      }
    },
    [onTabNext, onTabPrev],
  );

  const hasContent = value !== null && value !== '';
  const visible    = hasContent || showHint || isFocused;

  if (!visible) return null;

  return (
    <div
      className="print:block"
      style={{
        paddingTop: 4,
        paddingBottom: 2,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <textarea
        ref={textareaRef}
        value={value ?? ''}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={showHint || isFocused ? 'Lyrics…' : ''}
        rows={1}
        spellCheck={true}
        style={{
          display: 'block',
          width: '100%',
          resize: 'none',
          overflow: 'hidden',
          background: 'transparent',
          border: 'none',
          outline: 'none',
          padding: '2px 4px',
          fontSize: 12,
          lineHeight: 1.5,
          fontFamily: 'var(--font-sans)',
          fontStyle: 'italic',
          color: hasContent
            ? 'var(--color-ink-secondary, var(--color-ink-primary))'
            : 'var(--color-ink-muted)',
          opacity: hasContent ? 0.75 : 0.45,
          borderTop: isFocused || hasContent
            ? '1px dashed var(--color-rule)'
            : '1px dashed transparent',
          transition: 'opacity 0.15s, border-color 0.15s',
          boxSizing: 'border-box',
          minHeight: 22,
        }}
      />
    </div>
  );
}
